/* Header search modal with predictive suggestions from /search/suggest.
   A custom element (<antarti-search>, snippets/search-modal.liquid) so a header re-render
   in the theme editor tears down the old listeners and binds the new dialog. */

(() => {
  'use strict';

  class AntartiSearch extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('[data-search-modal]');
      if (!this.modal) return;
      this.input = this.modal.querySelector('[data-search-input]');
      this.results = this.modal.querySelector('[data-search-results]');
      this.hint = this.modal.querySelector('[data-search-hint]');
      this.status = this.modal.querySelector('[data-search-status]');
      this.timer = null;
      this.controller = null;

      /* The open buttons live elsewhere in the header, so listen on the document. */
      this.onDocClick = (e) => {
        if (e.target.closest('[data-search-open]')) {
          e.preventDefault();
          this.open();
        } else if (e.target.closest('[data-search-close]')) {
          this.modal.close();
        }
      };
      this.onBackdrop = (e) => {
        if (e.target === this.modal) this.modal.close();
      };
      this.onInput = () => {
        clearTimeout(this.timer);
        const q = this.input.value.trim();
        if (q.length < 2) {
          this.controller?.abort();
          this.results.innerHTML = '';
          if (this.hint) this.hint.hidden = false;
          this.announce('');
          return;
        }
        this.timer = setTimeout(() => this.suggest(q), 220);
      };
      /* Arrow keys move through suggestions; Enter follows the highlighted one. */
      this.onInputKey = (e) => {
        const links = Array.from(this.results.querySelectorAll('a'));
        if (!links.length || !['ArrowDown', 'ArrowUp'].includes(e.key)) return;
        e.preventDefault();
        const i = links.indexOf(document.activeElement);
        const next = e.key === 'ArrowDown' ? links[i + 1] || links[0] : links[i - 1] || links[links.length - 1];
        next.focus();
      };
      this.onResultsKey = (e) => {
        const links = Array.from(this.results.querySelectorAll('a'));
        const i = links.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          (links[i + 1] || this.input).focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          (links[i - 1] || this.input).focus();
        }
      };

      document.addEventListener('click', this.onDocClick);
      this.modal.addEventListener('click', this.onBackdrop);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onInputKey);
      this.results.addEventListener('keydown', this.onResultsKey);
    }

    disconnectedCallback() {
      if (!this.modal) return;
      clearTimeout(this.timer);
      cancelAnimationFrame(this.announceFrame);
      this.controller?.abort();
      document.removeEventListener('click', this.onDocClick);
      this.modal.removeEventListener('click', this.onBackdrop);
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('keydown', this.onInputKey);
      this.results.removeEventListener('keydown', this.onResultsKey);
    }

    open() {
      if (this.modal.open) return;
      this.modal.showModal();
      this.input.focus();
    }

    async suggest(q) {
      this.controller?.abort();
      const controller = (this.controller = new AbortController());
      const url = `${window.Shopify?.routes?.root || '/'}search/suggest?q=${encodeURIComponent(q)}&resources[type]=product,page,article&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`;
      try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) return;
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        /* The box may have been cleared or changed while this was in flight. */
        if (controller.signal.aborted || this.input.value.trim() !== q) return;
        const fresh = html.querySelector('[data-predictive-results]');
        this.results.innerHTML = fresh ? fresh.outerHTML : '';
        if (this.hint) this.hint.hidden = !!this.results.innerHTML;
        this.announce(fresh ? fresh.dataset.announce || '' : '');
      } catch {
        /* aborted or offline */
      }
    }

    /* Focus stays in the box, so the status line says what appeared (WCAG 4.1.3). Emptied
       first so the same count twice in a row is still read out. */
    announce(text) {
      if (!this.status) return;
      cancelAnimationFrame(this.announceFrame);
      this.status.textContent = '';
      if (!text) return;
      this.announceFrame = requestAnimationFrame(() => {
        this.status.textContent = text;
      });
    }
  }

  (window.antarti?.define || ((n, c) => customElements.get(n) || customElements.define(n, c)))('antarti-search', AntartiSearch);
})();
